import { defaultStrokeColor, mapActive } from "../utils/map-utils";
import { RootStore } from "../../shared/stores/RootStore";
import {
  Feature,
  GeoJsonProperties,
  LineString,
  MultiPolygon,
  Polygon,
  Position,
} from "geojson";
import { BBoxSelection } from "./BBoxSelection";
import { CustomSource } from "../models/CustomSource";
import {
  buildDefaultCategories,
  CompositeCategory,
} from "../../project/models/CompositeCategory";
import { categorizeCompositeFeature } from "../utils/query/categorizeCompositeFeature";
import { findAdjacentFeatures } from "../utils/query/findAdjacentFeatures";
import { relevantWayLayers } from "../utils/mapbox";
import {
  buildEmptyCreateHlaWay,
  CreateHlaWay,
  HlaWay,
} from "../../hla/models/HlaWay";
import {
  delay,
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  HeavyVehicleFlowType,
} from "@keita/lanepatrol-sdk";
import {
  buildCreateOrUpdateHlaLane,
  PresenceDistance,
} from "../../hla/models/HlaLane";
import { HlaWayType } from "../../hla/models/HlaWay";
import { GeoJSONFeature } from "mapbox-gl";
import { applyAdjacentFeaturesToLane } from "../utils/query/applyAdjacentFeaturesToLane";
import { toast, toaster } from "../../shared/views/CustomToaster";
import turf from "../../shared/utils/turf";
import { polygonToBbox } from "../utils/query/polygonToBbox";
import { AllGeoJSON } from "@turf/helpers";
import { MapMode } from "../../project/stores/MapStore";

type BBox = [mapboxgl.Point, mapboxgl.Point];
type Side = "left" | "right";

export const originColor = "#1ed4a0";
export const destinationColor = "#b880ec";

export class QueryInfrastructureMapSource {
  map: mapboxgl.Map;
  rootStore: RootStore;
  interactions: string[] = [];
  bboxSelection?: BBoxSelection;
  bbox?: BBox;
  featuresInSelection: GeoJSONFeature[] = [];
  activeLayers: string[] = [];

  rightHandTraffic = true;
  frontSide: Side = "right";
  backSide: Side = "left";

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;

    this.map.addLayer(parkingLayer);
    this.map.addLayer(busStopLayer);
  }

  clear() {
    this.map.removeLayer(parkingLayer.id);
    this.map.removeLayer(busStopLayer.id);
    this.disable();
  }

  disable() {
    this.bbox = undefined;
    this.featuresInSelection = [];
    this.clearInteractions();
    this.clearActiveLayers();
    this.removeOriginDestinationSource();

    if (this.map.getSource(sourceName)) {
      this.map.removeLayer(fillLayerConfig.id);
      this.map.removeLayer(lineLayerConfig.id);
      this.map.removeSource(sourceName);
    }
  }

  clearInteractions() {
    this.interactions.map((name) => this.map.removeInteraction(name));

    this.bboxSelection?.disable();
    this.bboxSelection = undefined;
  }

  onModeChange(mode: MapMode) {
    this.disable();
    if (mode === MapMode.QueryHlaBbox) {
      this.enableBBoxInteraction();
    } else if (mode === MapMode.QueryHlaOriginDestination) {
      this.addOriginDestinationSource();
    }
  }

  applyProjectConfig() {
    const { projectStore } = this.rootStore;

    this.rightHandTraffic = projectStore.rightHandTraffic;
    this.frontSide = projectStore.rightHandTraffic ? "right" : "left";
    this.backSide = projectStore.rightHandTraffic ? "left" : "right";
  }

  enableBBoxInteraction() {
    const { mapQueryStore } = this.rootStore;

    this.applyProjectConfig();

    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(fillLayerConfig);
      this.map.addLayer(lineLayerConfig);
    }

    this.bboxSelection = new BBoxSelection(this.map, {
      onDrag: (bbox) => {
        this.setBBox(bbox);
        this.featuresInSelection = this.queryLayers(bbox);

        const categories = this.extractCompositeCategories(
          this.featuresInSelection,
        );
        mapQueryStore.setCompositeCategories(categories);
      },
      onClick: (point) => {
        this.setSourceData(undefined);
      },
    });

    this.bboxSelection.enable();
  }

  private clearActiveLayers() {
    this.activeLayers.forEach((layerName) => {
      this.map.removeLayer(layerName);
    });
    this.activeLayers = [];
  }

  highlightCategories(categories: CompositeCategory[]) {
    this.clearActiveLayers();

    categories.forEach((category) => {
      if (category.enabled) {
        category.items.forEach((item) => {
          const layerId = `highlight-${item.class}-${item.type}`;
          this.activeLayers.push(layerId);
          this.map.addLayer(
            buildHighlightLayerSpec(layerId, category.color, [
              "all",
              ["==", "class", item.class],
              ["==", "type", item.type],
            ]),
          );
        });
      }

      if (category.name === HlaWayType.Cycleway && category.enabled) {
        const layerId = `highlight-attached-cycleways`;
        this.activeLayers.push(layerId);
        this.map.addLayer(
          buildHighlightLayerSpec(layerId, category.color, [
            "all",
            ["has", "bike_lane"],
            ["!=", "bike_lane", "no"],
          ]),
        );
      }
    });
  }

  async importSelectedCategories() {
    if (!this.bbox) return;

    const { mapQueryStore, hlaStore, campaignStore } = this.rootStore;

    const totalFeatures = this.featuresInSelection.length;
    var featureCounter = 0;
    var wayCounter = 0;

    console.log("Starting import of bbox", this.bbox);

    const toastId = toast(
      "loading",
      `Querying your area selection. Please wait...`,
    );

    const campaign = campaignStore.selected!;

    const enabledCategories = mapQueryStore.compositeCategories
      .filter((cc) => cc.enabled)
      .map((cc) => cc.name);

    const bboxPolygon = this.pixelBBoxToLonLat(this.bbox);

    for (const feature of this.featuresInSelection) {
      const featureId = feature.id as number;
      const linesInsideBBox: Position[][] = [];

      // Roads may come in groups and some of the lines in each group may be outside the bbox
      if (feature.geometry.type === "LineString") {
        linesInsideBBox.push(feature.geometry.coordinates);
      } else if (feature.geometry.type === "MultiLineString") {
        for (const coordinates of feature.geometry.coordinates) {
          const lineString = turf.lineString(coordinates);

          // We ignore the lines that are outside the bbox
          if (turf.booleanIntersects(lineString, bboxPolygon)) {
            linesInsideBBox.push(coordinates);
          }
        }
      }

      for (const line of linesInsideBBox) {
        const way = await this.analyzeFeature(
          featureId,
          line,
          feature.properties,
          enabledCategories,
        );

        if (way) {
          console.log("Resulting way", way);

          way.campaignId = campaign.id;
          // TODO comment
          // this.addRequestToMap(way);
          // TODO uncomment
          await hlaStore.create(way);
          wayCounter++;
        }
      }

      featureCounter++;

      toaster.update(toastId, {
        title: `Processing features (${featureCounter} of ${totalFeatures})`,
      });
    }

    toaster.update(toastId, {
      type: "success",
      title: `Done! ${wayCounter} ways imported`,
    });

    hlaStore.updateMapSource();
    mapQueryStore.deactivate();
  }

  centerMapAround(geoJson: AllGeoJSON) {
    const featureCenter = turf.centroid(geoJson);
    this.map.jumpTo({
      center: featureCenter.geometry.coordinates as mapboxgl.LngLatLike,
      zoom: 16,
    });
  }

  addOriginDestinationSource() {
    if (mapActive(this.map) && !this.map.getSource(originDestinationSource)) {
      this.map.addSource(originDestinationSource, sourceConfig);
      this.map.addLayer(routeStrokeLayerConfig);
      this.map.addLayer(routeLineLayerConfig);
      this.map.addLayer(originDestinationStrokeLayerConfig);
      this.map.addLayer(originDestinationLayerConfig);
      this.map.addLayer(originDestinationTextLayerConfig);
    }
  }

  removeOriginDestinationSource() {
    if (this.map.getSource(originDestinationSource)) {
      this.map.removeLayer(routeStrokeLayerConfig.id);
      this.map.removeLayer(routeLineLayerConfig.id);
      this.map.removeLayer(originDestinationStrokeLayerConfig.id);
      this.map.removeLayer(originDestinationLayerConfig.id);
      this.map.removeLayer(originDestinationTextLayerConfig.id);

      this.map.removeSource(originDestinationSource);
    }
  }

  awaitPointSelection(onSelection: (point: mapboxgl.LngLat) => void) {
    this.applyProjectConfig();

    const canvas = this.map.getCanvasContainer();
    canvas.style.cursor = "pointer";

    const interactionId = "set-point";

    this.interactions.push(interactionId);
    this.map.removeInteraction(interactionId);
    this.map.addInteraction(interactionId, {
      type: "click",
      handler: (e) => {
        const lnglat = this.map.unproject(e.point);
        onSelection(lnglat);
        canvas.style.cursor = "grab";
        this.map.removeInteraction(interactionId);
      },
    });
  }

  async importRoute(route: Position[]) {
    if (route.length < 2) return;

    const routeLine = turf.lineString(route);

    this.centerMapAround(routeLine);

    const featureRefs = new Set<string>();
    const features = route.flatMap((position, index) => {
      if (index === route.length - 1) return [];

      const nextPosition = route[index + 1];
      const segment = [position, nextPosition];
      const lineString = turf.lineString(segment);

      const features = this.queryFeaturesAlongLine(lineString);

      // remove duplicated
      const filteredFeatures = features.filter(
        (f) => !featureRefs.has(JSON.stringify(f.geometry)),
      );
      filteredFeatures.forEach((f) =>
        featureRefs.add(JSON.stringify(f.geometry)),
      );

      return filteredFeatures;
    });

    const { mapQueryStore, hlaStore, campaignStore } = this.rootStore;
    const totalFeatures = features.length;
    let featureCounter = 0;
    let wayCounter = 0;

    const toastId = toast(
      "loading",
      `Querying route selection. Please wait...`,
    );

    const campaign = campaignStore.selected!;
    const enabledCategories = Object.values(HlaWayType);

    for (const feature of features) {
      const featureId = feature.id as number;
      const linesInsideRoute: Position[][] = [];
      if (feature.geometry.type === "LineString") {
        linesInsideRoute.push(feature.geometry.coordinates);
      } else if (feature.geometry.type === "MultiLineString") {
        for (const coordinates of feature.geometry.coordinates) {
          const lineString = turf.lineString(coordinates);
          if (turf.booleanIntersects(lineString, routeLine)) {
            linesInsideRoute.push(coordinates);
          }
        }
      }
      for (const line of linesInsideRoute) {
        const way = await this.analyzeFeature(
          featureId,
          line,
          feature.properties,
          enabledCategories,
        );
        if (way) {
          way.campaignId = campaign.id;
          await hlaStore.create(way);
          // this.addRequestToMap(way);
          wayCounter++;
        }
      }
      featureCounter++;
      toaster.update(toastId, {
        title: `Processing features (${featureCounter} of ${totalFeatures})`,
      });
    }

    toaster.update(toastId, {
      type: "success",
      title: `Done! ${wayCounter} ways imported`,
    });

    hlaStore.updateMapSource();
    mapQueryStore.deactivate();
  }

  private trimLine(
    lineFeature: Feature<LineString>,
    trimMeters = 3,
  ): Feature<LineString> {
    const totalLength = turf.length(lineFeature, { units: "meters" });

    // Ensure the line is long enough to trim
    if (totalLength <= trimMeters * 2) {
      return lineFeature;
    }

    // Slice from trimMeters to (totalLength - trimMeters)
    const trimmed = turf.lineSliceAlong(
      lineFeature,
      trimMeters,
      totalLength - trimMeters,
      { units: "meters" },
    );

    return trimmed;
  }

  private flattenLineAndMultiLineFeatures(
    features: Feature[],
  ): Feature<LineString>[] {
    return features.flatMap((feat) => {
      if (feat.geometry.type === "LineString") {
        const lineString = feat as Feature<LineString>;
        return [lineString];
      } else if (feat.geometry.type === "MultiLineString") {
        return feat.geometry.coordinates.map((coordinates) => {
          return turf.lineString(coordinates, feat.properties);
        });
      }
      return [];
    });
  }

  async importPosition(position: Position): Promise<HlaWay[]> {
    const { mapQueryStore, hlaStore, campaignStore, mapStore } = this.rootStore;
    const point = turf.point(position);

    this.centerMapAround(point);

    const buffer = turf.buffer(point, 5, { units: "meters" })!;
    const bbox = polygonToBbox(buffer, mapStore.map!);
    const features: Feature<LineString>[] =
      this.flattenLineAndMultiLineFeatures(
        mapStore.map!.queryRenderedFeatures(bbox, {
          layers: relevantWayLayers,
        }),
      ).filter((feat) => turf.booleanIntersects(feat, buffer));

    const totalFeatures = features.length;
    let featureCounter = 0;
    let wayCounter = 0;

    const toastId = toast(
      "loading",
      `Querying current position. Please wait...`,
    );

    const campaign = campaignStore.selected!;
    const enabledCategories = Object.values(HlaWayType);
    const importedWays: HlaWay[] = [];

    for (const feature of features) {
      const way = await this.analyzeFeature(
        feature.id as number,
        feature.geometry.coordinates,
        feature.properties,
        enabledCategories,
      );
      if (way) {
        way.campaignId = campaign.id;
        const newWay = await hlaStore.create(way);
        importedWays.push(newWay);
        // this.addRequestToMap(way);
        wayCounter++;
      }

      featureCounter++;
      toaster.update(toastId, {
        title: `Processing features (${featureCounter} of ${totalFeatures})`,
      });
    }

    if (wayCounter > 0) {
      toaster.update(toastId, {
        type: "success",
        title: `Done! ${wayCounter} ways imported`,
      });
    } else {
      toaster.update(toastId, {
        type: "warning",
        title: `No ways were found in this location...`,
      });
    }

    hlaStore.updateMapSource();
    mapQueryStore.deactivate();

    return importedWays;
  }

  private queryFeaturesAlongLine(routeLine: Feature<LineString>) {
    // search for features near the center of the segment
    const trimmedLine = this.trimLine(routeLine);
    const bufferPolygon = turf.buffer(trimmedLine, 2, {
      units: "meters",
    })!;

    const bbox = polygonToBbox(bufferPolygon, this.map);
    const candidates = this.map.queryRenderedFeatures(bbox, {
      layers: relevantWayLayers,
    });

    return candidates.filter(
      (feature) =>
        this.featureIntersectsPolygon(feature, bufferPolygon) &&
        this.isFeatureAlongLine(feature, routeLine),
    );
  }

  private featureIntersectsPolygon(
    feature: mapboxgl.GeoJSONFeature,
    bufferPolygon: Feature<Polygon | MultiPolygon>,
  ) {
    if (!feature.geometry) return false;

    if (feature.geometry.type === "LineString") {
      return turf.booleanIntersects(feature.geometry, bufferPolygon);
    }

    if (feature.geometry.type === "MultiLineString") {
      return feature.geometry.coordinates.some((coords) =>
        turf.booleanIntersects(turf.lineString(coords), bufferPolygon),
      );
    }

    return false;
  }

  private isFeatureAlongLine(
    feature: mapboxgl.GeoJSONFeature,
    routeLine: Feature<LineString>,
  ) {
    if (!feature.geometry) return false;

    const lineToTheLeft = turf.lineOffset(routeLine.geometry, -8, {
      units: "meters",
    });
    const lineToTheRight = turf.lineOffset(routeLine.geometry, 8, {
      units: "meters",
    });

    if (feature.geometry.type === "LineString") {
      return (
        !turf.booleanIntersects(feature.geometry, lineToTheLeft) &&
        !turf.booleanIntersects(feature.geometry, lineToTheRight)
      );
    }

    if (feature.geometry.type === "MultiLineString") {
      return !feature.geometry.coordinates.some(
        (coords) =>
          turf.booleanIntersects(turf.lineString(coords), lineToTheLeft) ||
          turf.booleanIntersects(turf.lineString(coords), lineToTheRight),
      );
    }

    return false;
  }

  private pixelBBoxToLonLat(bbox: BBox): Feature<Polygon> {
    const { x: minX, y: minY } = bbox[0];
    const { x: maxX, y: maxY } = bbox[1];

    const sw = this.map.unproject([minX, maxY]);
    const ne = this.map.unproject([maxX, minY]);

    return turf.bboxPolygon([sw.lng, sw.lat, ne.lng, ne.lat]);
  }

  private async analyzeFeature(
    featureId: number,
    coordinates: Position[],
    properties: GeoJsonProperties,
    enabledCategories: HlaWayType[],
  ) {
    const includeCycleways = enabledCategories.includes(HlaWayType.Cycleway);

    const props = properties!;
    const type = props.type ?? "";
    const className = props.class ?? "";

    var categoryName = categorizeCompositeFeature(className, type);

    const roadWithCyclingInfrastructure =
      !!props.bike_lane && props.bike_lane !== "no";
    const shouldCreateCurrentLane = enabledCategories.includes(categoryName);

    if (
      !shouldCreateCurrentLane &&
      !(includeCycleways && roadWithCyclingInfrastructure)
    ) {
      console.log("Ignoring", categoryName);
      return;
    }

    const lineString: LineString = {
      type: "LineString",
      coordinates,
    };

    console.log("Waiting and moving...");
    const featureCenter = turf.centroid(lineString);
    this.map.jumpTo({
      center: featureCenter.geometry.coordinates as mapboxgl.LngLatLike,
      zoom: 16,
    });
    await delay(200);

    const way = buildEmptyCreateHlaWay();

    console.log("Importing:", categoryName, props);

    way.coordinates = coordinates;
    way.lengthM = turf.length(
      {
        type: "Feature",
        geometry: lineString,
        properties: null,
      },
      {
        units: "meters",
      },
    );

    way.type = categoryName;

    // Name
    if (props.name) {
      way.name = props.name;
    }

    way.frontFacingLane = buildCreateOrUpdateHlaLane(true);

    // Flow direction
    if (props.oneway) {
      way.frontFacingLane.flowDirection =
        props.oneway === "true"
          ? FlowDirectionType.OneWay
          : FlowDirectionType.TwoWay;
    }

    // Facility type and width
    if (categoryName === HlaWayType.Cycleway) {
      way.frontFacingLane.facilityType = FacilityType.OffRoadBicyclePath;
    } else if (
      categoryName === HlaWayType.Service ||
      categoryName === HlaWayType.Motorway ||
      categoryName === HlaWayType.Primary ||
      categoryName === HlaWayType.Secondary ||
      categoryName === HlaWayType.Tertiary ||
      categoryName === HlaWayType.Unclassified
    ) {
      way.frontFacingLane.facilityType = FacilityType.MixedTrafficRoadLane;
      way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Wide;
    } else if (categoryName === HlaWayType.Sidewalk) {
      way.frontFacingLane.facilityType = FacilityType.Sidewalk;
      way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
    } else if (
      categoryName === HlaWayType.Pedestrian ||
      categoryName === HlaWayType.Platform ||
      categoryName === HlaWayType.FootPath
    ) {
      way.frontFacingLane.facilityType = FacilityType.MultiUsePath;
      if (categoryName === HlaWayType.Pedestrian) {
        way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Wide;
      } else {
        way.frontFacingLane.facilityWidthPerDirection =
          FacilityWidth.VeryNarrow;
      }
    }

    if (roadWithCyclingInfrastructure && includeCycleways) {
      if (props.bike_lane === this.frontSide || props.bike_lane === "both") {
        way.frontFacingLane.facilityType = FacilityType.OnRoadBicycleLane;
        way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
        way.frontFacingLane.flowDirection = FlowDirectionType.OneWay;

        if (this.rightHandTraffic) {
          way.frontFacingLane.adjacentFeaturesLeft.roadLane =
            PresenceDistance.From0to1m;
        } else {
          way.frontFacingLane.adjacentFeaturesRight.roadLane =
            PresenceDistance.From0to1m;
        }
      }

      if (props.bike_lane === this.backSide || props.bike_lane === "both") {
        way.backFacingLane = buildCreateOrUpdateHlaLane(false);
        way.backFacingLane.facilityType = FacilityType.OnRoadBicycleLane;
        way.backFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
        way.backFacingLane.flowDirection = FlowDirectionType.OneWay;

        if (this.rightHandTraffic) {
          way.backFacingLane.adjacentFeaturesLeft.roadLane =
            PresenceDistance.From0to1m;
        } else {
          way.backFacingLane.adjacentFeaturesRight.roadLane =
            PresenceDistance.From0to1m;
        }
      }
    }

    const adjacentFeatures = findAdjacentFeatures(
      featureId,
      lineString,
      this.map,
    );

    console.log("adjacentFeatures", adjacentFeatures);

    if (adjacentFeatures.BusStop.left || adjacentFeatures.BusStop.right) {
      way.heavyVehicleFlow = HeavyVehicleFlowType.MediumToHigh;
    }

    if (way.frontFacingLane) {
      applyAdjacentFeaturesToLane(way.frontFacingLane, adjacentFeatures);
    }

    if (way.backFacingLane) {
      applyAdjacentFeaturesToLane(way.backFacingLane, adjacentFeatures);
    }

    return way;
  }

  // just for testing
  private addRequestToMap(way: CreateHlaWay) {
    const { hlaStore } = this.rootStore;
    const wayId = Math.random().toString();

    hlaStore.setWays(
      hlaStore.ways.concat([
        {
          ...way,
          id: wayId,
          frontFacingLane: way.frontFacingLane
            ? {
                ...way.frontFacingLane,
                hlaWayId: wayId,
                score: null,
              }
            : null,
          backFacingLane: way.backFacingLane
            ? {
                ...way.backFacingLane,
                hlaWayId: wayId,
                score: null,
              }
            : null,
          createdAt: "",
        },
      ]),
    );
  }

  private queryLayers(bbox: BBox) {
    const features = this.map.queryRenderedFeatures(bbox, {
      layers: relevantWayLayers,
    });

    console.log("All queried features:", features);

    return features;
  }

  private extractCompositeCategories(features: mapboxgl.GeoJSONFeature[]) {
    const totalFeaturesPerType: Record<string, number> = {};
    const classToTypes: Record<string, Set<string>> = {};
    var totalRoadsWithBikeLanes = 0;

    const types: Set<string> = new Set();
    features.forEach((feature) => {
      const className = feature.properties!.class;
      const type = feature.properties!.type;
      const hasBikeLane =
        !!feature.properties?.bike_lane &&
        feature.properties.bike_lane !== "no";
      classToTypes[className] = classToTypes[className] ?? new Set();
      classToTypes[className].add(type);
      types.add(type);

      totalFeaturesPerType[type] = (totalFeaturesPerType[type] ?? 0) + 1;

      if (hasBikeLane) {
        totalRoadsWithBikeLanes++;
      }
    });

    const allCategories = buildDefaultCategories();

    const categoriesByName = new Map<string, CompositeCategory>(
      allCategories.map((c) => [c.name, c]),
    );

    for (const [className, types] of Object.entries(classToTypes)) {
      for (const type of types) {
        var name = categorizeCompositeFeature(className, type);

        const totalFeatures = totalFeaturesPerType[type];

        const category = categoriesByName.get(name)!;
        category.totalFeatures += totalFeatures;
        category.items.push({ class: className, type: type });
      }
    }

    const cyclewayCategory = categoriesByName.get(HlaWayType.Cycleway)!;
    cyclewayCategory.totalFeatures += totalRoadsWithBikeLanes;

    return [...categoriesByName.values()];
  }

  private setBBox(bbox: BBox) {
    this.bbox = bbox;

    const { mapQueryStore } = this.rootStore;
    const [pointA, pointB] = bbox;

    const coordsA = this.map.unproject([pointA.x, pointA.y]);
    const coordsB = this.map.unproject([pointB.x, pointB.y]);

    const polygon: Polygon = {
      type: "Polygon",
      coordinates: [
        [
          [coordsA.lng, coordsA.lat],
          [coordsB.lng, coordsA.lat],
          [coordsB.lng, coordsB.lat],
          [coordsA.lng, coordsB.lat],
          [coordsA.lng, coordsA.lat],
        ],
      ],
    };

    mapQueryStore.setPolygon(polygon.coordinates[0]); // TODO can this be removed?

    this.setSourceData(polygon);
  }

  setSourceData(geometry: Polygon | LineString | undefined) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features: geometry
          ? [
              {
                type: "Feature",
                geometry,
                properties: {},
              },
            ]
          : [],
      });
    }
  }

  setOriginDestinationData(
    origin: mapboxgl.LngLat | undefined,
    destination: mapboxgl.LngLat | undefined,
    routes: LineString[],
    selectedRouteIndex: number,
  ) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(
        originDestinationSource,
      ) as mapboxgl.GeoJSONSource;

      const features: Feature[] = [];

      if (origin) {
        features.push(
          turf.point([origin.lng, origin.lat], {
            color: originColor,
            text: "A",
          }),
        );
      }

      if (destination) {
        features.push(
          turf.point([destination.lng, destination.lat], {
            color: destinationColor,
            text: "B",
          }),
        );
      }

      routes.forEach((route, index) => {
        features.push(
          turf.lineString(route.coordinates, {
            selected: index === selectedRouteIndex,
          }),
        );
      });

      source?.setData({
        type: "FeatureCollection",
        features: features,
      });
    }
  }
}

function buildHighlightLayerSpec(
  id: string,
  color: string,
  filter: mapboxgl.FilterSpecification,
): mapboxgl.LineLayerSpecification {
  return {
    id,
    filter,
    type: "line",
    source: "composite",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": color,
      "line-width": 4,
      "line-opacity": 0.8,
    },
    "source-layer": "road",
  };
}

const sourceName = CustomSource.QueryInfrastructure;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const fillLayerName = `${sourceName}-fill`;
const fillLayerConfig: mapboxgl.FillLayerSpecification = {
  type: "fill",
  paint: {
    "fill-opacity": 0.1,
    "fill-color": "#12d3c4",
  },
  source: sourceName,
  id: fillLayerName,
};

const lineLayerName = `${sourceName}-line`;
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  type: "line",
  paint: {
    "line-opacity": 0.5,
    "line-color": "#047067",
  },
  source: sourceName,
  id: lineLayerName,
};

const isLineString = ["==", "$type", "LineString"];
const isRouteSelected = ["boolean", ["get", "selected"], false];
const originDestinationSource = CustomSource.QueryHlaOriginDestination;
const routeLineLayerName = `${originDestinationSource}-line`;
const routeLineLayerConfig: mapboxgl.LineLayerSpecification = {
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-width": 5,
    "line-color": ["case", isRouteSelected, "#10a1d6", "#3b8ca9"],
  },
  source: originDestinationSource,
  id: routeLineLayerName,
  filter: isLineString,
};

const routeStrokeLayerName = `${originDestinationSource}-stroke`;
const routeStrokeLayerConfig: mapboxgl.LineLayerSpecification = {
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-width": 7,
    "line-color": ["case", isRouteSelected, "#1c4cec", "#3d57ad"],
  },
  source: originDestinationSource,
  id: routeStrokeLayerName,
  filter: isLineString,
};

const isPoint = ["==", "$type", "Point"];
const originDestinationLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: `${originDestinationSource}-point`,
  type: "circle",
  source: originDestinationSource,
  paint: {
    "circle-radius": 8,
    "circle-color": ["get", "color"],
    "circle-opacity": 0.8,
  },
  filter: isPoint,
};

const originDestinationStrokeLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: `${originDestinationSource}-point-stroke`,
  type: "circle",
  source: originDestinationSource,
  paint: {
    "circle-radius": 9,
    "circle-color": defaultStrokeColor,
  },
  filter: isPoint,
};

const originDestinationTextLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: `${originDestinationSource}-text`,
  type: "symbol",
  source: originDestinationSource,
  layout: { "text-field": ["get", "text"], "text-size": 14 },
  paint: {
    "text-color": "#ffffff",
  },
  filter: isPoint,
};

const parkingLayer: mapboxgl.CircleLayerSpecification = {
  type: "circle",
  paint: {
    "circle-radius": 7,
    "circle-opacity": 0,
    "circle-color": "transparent",
  },
  id: CustomSource.Parking,
  source: "composite",
  "source-layer": "poi_label",
  filter: ["==", "type", "Parking"],
};

const busStopLayer: mapboxgl.CircleLayerSpecification = {
  type: "circle",
  paint: {
    "circle-radius": 7,
    "circle-opacity": 0,
    "circle-color": "transparent",
  },
  id: CustomSource.BusStop,
  source: "composite",
  "source-layer": "transit_stop_label",
};

// const testLayerName = `test`;
// const testLayerConfig: mapboxgl.LineLayerSpecification = {
//   type: "line",
//   layout: {
//     "line-join": "round",
//     "line-cap": "round",
//   },
//   paint: {
//     "line-color": "#b81fe7",
//     "line-width": 5,
//   },
//   id: testLayerName,
//   source: "composite",
//   "source-layer": "road",
//   filter: ["all", isLineString, ["==", "type", "residential"]],
// };
